/**
 * Frontend soft guards for MarketFit guided flow.
 * Blocks advance until required fields are present. Backend guards are stubbed.
 */

const CLAIMED_EFFECT_VERBS = [
  'reduces',
  'eliminates',
  'accelerates',
  'replaces',
  'enables',
  'improves',
  'saves',
  'increases',
  'decreases',
  'simplifies',
  'automates',
  'optimizes',
];

const PRODUCT_LEAKAGE_PATTERNS = [
  /\b(our|my|the)\s+(product|solution|app|tool|platform|software)\b/i,
  /\b(it|this)\s+(helps|solves|fixes|improves)\b/i,
  /\b(product|solution)\s+(that|which)\s+/i,
  // Benefit framing
  /\b(with|by)\s+(our|my|the)\s+(tool|system|app|platform|solution)\b/i,
  /\b(using|via)\s+(our|my|the)\s+(tool|system|app|platform)\b/i,
  /\b(implementing|adopting)\s+(our|my|the)\s+/i,
  // Product brand reuse
  /\b(our|my)\s+(product|solution)\s+(name|brand)\b/i,
];

const GENERIC_PRODUCT_NAMES = new Set(['product', 'solution', 'app', 'tool', 'platform', 'software', 'system']);

function escapeRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function extractProductName(productBelief: unknown): string | null {
  if (!productBelief || typeof productBelief !== 'object') return null;
  const obj = productBelief as Record<string, unknown>;
  const name = (obj.name ?? obj.product_name ?? obj.productName ?? '').toString().trim();
  if (name.length < 2) return null;
  const lower = name.toLowerCase();
  if (GENERIC_PRODUCT_NAMES.has(lower)) return null;
  return name;
}

function checkProductLeakage(
  situationText: string,
  productBelief?: unknown
): boolean {
  if (PRODUCT_LEAKAGE_PATTERNS.some((p) => p.test(situationText))) return true;
  const productName = productBelief ? extractProductName(productBelief) : null;
  if (productName) {
    try {
      const re = new RegExp(escapeRegex(productName), 'i');
      if (re.test(situationText)) return true;
    } catch {
      // invalid regex, skip
    }
  }
  return false;
}

/** Extract significant nouns/phrases after claimed effect verbs from product_belief */
function extractProductEffectTargets(productBelief: unknown): string[] {
  const text = typeof productBelief === 'string'
    ? productBelief
    : typeof productBelief === 'object' && productBelief !== null
      ? JSON.stringify(productBelief)
      : '';
  const targets: string[] = [];
  for (const verb of CLAIMED_EFFECT_VERBS) {
    const re = new RegExp(`\\b${verb}[a-z]*\\s+([a-z][a-z\\s]{2,40}?)(?:\\.|,|$|and|or)`, 'gi');
    let m: RegExpExecArray | null;
    while ((m = re.exec(text)) !== null) {
      const phrase = m[1].trim().toLowerCase();
      if (phrase.length > 3 && !targets.includes(phrase)) targets.push(phrase);
    }
  }
  // Also check claimed_effect, mechanism, target_constraint if object
  if (typeof productBelief === 'object' && productBelief !== null) {
    const obj = productBelief as Record<string, unknown>;
    for (const k of ['claimed_effect', 'mechanism', 'target_constraint']) {
      const v = obj[k];
      if (typeof v === 'string' && v.trim().length > 2) {
        const words = v.toLowerCase().split(/\s+/).filter((w) => w.length > 2);
        words.forEach((w) => { if (!targets.includes(w)) targets.push(w); });
      }
    }
  }
  return targets;
}

/** Extract constraint/cost keywords from situation_boundary and situation_value */
function extractSituationConstraintKeywords(sessionState: Record<string, unknown>): string[] {
  const keywords: string[] = [];
  for (const key of ['situation_boundary', 'situation_value']) {
    const val = sessionState[key];
    const text = typeof val === 'string' ? val : typeof val === 'object' && val !== null ? JSON.stringify(val) : '';
    const words = text.toLowerCase().match(/\b[a-z]{3,}\b/g) || [];
    words.forEach((w) => { if (!keywords.includes(w)) keywords.push(w); });
  }
  return keywords;
}

/** Check if product effect targets overlap with situation constraints (simple word match) */
function productEffectLinksToSituation(
  productBelief: unknown,
  sessionState: Record<string, unknown>
): boolean {
  const targets = extractProductEffectTargets(productBelief);
  const situationKeywords = extractSituationConstraintKeywords(sessionState);
  if (targets.length === 0 || situationKeywords.length === 0) return true; // no data, no warning
  for (const t of targets) {
    const tWords = t.split(/\s+/);
    for (const tw of tWords) {
      if (tw.length < 3) continue;
      if (situationKeywords.some((sk) => sk.includes(tw) || tw.includes(sk))) return true;
    }
  }
  return false;
}

/** Get situation scope hint from situation_boundary/situation_value (local/regional/global) */
function getSituationScopeHint(sessionState: Record<string, unknown>): string | null {
  for (const key of ['situation_boundary', 'situation_value']) {
    const val = sessionState[key];
    const text = typeof val === 'string' ? val : typeof val === 'object' && val !== null ? JSON.stringify(val) : '';
    if (/\b(global|regional|local|national)\b/i.test(text)) {
      const m = text.match(/\b(global|regional|local|national)\b/i);
      return m ? m[1].toLowerCase() : null;
    }
  }
  return null;
}

/** Get authority_scope of primary actor (first) */
function getPrimaryActorAuthorityScope(actors: unknown): string | null {
  const arr = Array.isArray(actors) ? actors : [];
  const first = arr[0];
  if (typeof first !== 'object' || first === null) return null;
  const scope = (first as Record<string, unknown>).authority_scope;
  return typeof scope === 'string' ? scope.toLowerCase() : null;
}

export interface GuardResult {
  canAdvance: boolean;
  reason?: string;
  productLeakageDetected?: boolean;
  /** Soft warning, does not block advance */
  crossStateWarning?: string;
}

/** Session state shape for guard checks; accepts API GuidedSessionState or Record */
export type GuidedSessionState = Record<string, unknown>;

/**
 * Check if the user can advance from the current state.
 * Implements simplified guard matrix for prototype.
 */
export function canAdvance(
  currentStateId: string,
  sessionState: Record<string, unknown>
): GuardResult {
  switch (currentStateId) {
    case 'ProductDefined': {
      const belief = sessionState.product_belief;
      const text = typeof belief === 'string' ? belief : typeof belief === 'object' && belief !== null
        ? JSON.stringify(belief)
        : '';
      const hasVerb = CLAIMED_EFFECT_VERBS.some((v) =>
        new RegExp(`\\b${v}[a-z]*\\b`, 'i').test(text)
      );
      return {
        canAdvance: hasVerb && text.trim().length > 20,
        reason: hasVerb ? undefined : 'Require at least one claimed effect (e.g. reduces, eliminates, accelerates)',
      };
    }

    case 'ActorIdentified': {
      const actors = sessionState.actors;
      const arr = Array.isArray(actors) ? actors : [];
      const hasAuthority = arr.some((a: unknown) => {
        const o = typeof a === 'object' && a !== null ? (a as Record<string, unknown>) : {};
        const role = String(o.role || o.authority || '').toLowerCase();
        const name = String(o.name || o.actor_name || '').trim();
        return name.length > 0 && (role.includes('payer') || role.includes('authority') || role.includes('decider') || role.length === 0);
      });
      return {
        canAdvance: arr.length >= 1 && (hasAuthority || arr.length > 0),
        reason: arr.length === 0 ? 'Require at least one actor with decision authority' : undefined,
      };
    }

    case 'SituationBounded': {
      const boundary = sessionState.situation_boundary;
      const text = typeof boundary === 'string' ? boundary : typeof boundary === 'object' && boundary !== null
        ? JSON.stringify(boundary)
        : '';
      const productLeakage = checkProductLeakage(text, sessionState.product_belief);
      const hasContent = text.trim().length > 30;
      const hasTriggerOrConstraint =
        /\b(trigger|when|frequency|constraint|cost|problem)\b/i.test(text) || hasContent;
      return {
        canAdvance: hasContent && hasTriggerOrConstraint && !productLeakage,
        reason: productLeakage
          ? 'Product reference detected in situation. Keep situation product-blind.'
          : !hasContent
            ? 'Describe the situation (context, frequency, triggers)'
            : undefined,
        productLeakageDetected: productLeakage,
      };
    }

    case 'SituationValued': {
      const value = sessionState.situation_value;
      const text = typeof value === 'string' ? value : typeof value === 'object' && value !== null
        ? JSON.stringify(value)
        : '';
      const hasCostOrFriction = /\b(cost|money|time|risk|friction|constraint)\b/i.test(text) || text.trim().length > 20;
      return {
        canAdvance: text.trim().length > 10 && hasCostOrFriction,
        reason: !hasCostOrFriction ? 'Require at least one cost or friction' : undefined,
      };
    }

    case 'ActorEmotionalBaseline': {
      const baseline = sessionState.actor_emotional_baseline;
      const text = typeof baseline === 'string' ? baseline : typeof baseline === 'object' && baseline !== null
        ? JSON.stringify(baseline)
        : '';
      const hasEmotion = /\b(anxiety|frustration|stress|fear|overwhelm|confidence|hope)\b/i.test(text) || text.trim().length > 20;
      return {
        canAdvance: text.trim().length > 10 && hasEmotion,
        reason: !hasEmotion ? 'Require at least one emotional friction' : undefined,
      };
    }

    case 'ProductSituationMapped': {
      const interventions = sessionState.functional_interventions;
      const interactions = sessionState.asset_interactions;
      const hasIntervention = Array.isArray(interventions) && interventions.length > 0;
      const hasInteraction = Array.isArray(interactions) && interactions.length > 0;
      const mapped = sessionState.functional_interventions || sessionState.asset_interactions;
      const hasAny = (Array.isArray(mapped) && mapped.length > 0) || hasIntervention || hasInteraction;
      const crossStateWarning =
        hasAny && !productEffectLinksToSituation(sessionState.product_belief, sessionState)
          ? 'Product effect not clearly linked to defined constraint.'
          : undefined;
      return {
        canAdvance: !!hasAny,
        reason: !hasAny ? 'Require at least one constraint interaction' : undefined,
        crossStateWarning,
      };
    }

    case 'EmotionalDeltaEvaluated': {
      const delta = sessionState.emotional_delta;
      const text = typeof delta === 'string' ? delta : typeof delta === 'object' && delta !== null
        ? JSON.stringify(delta)
        : '';
      const hasImprovement = /\b(better|improve|relief|confidence|hope|reduce)\b/i.test(text) || text.trim().length > 15;
      let crossStateWarning: string | undefined;
      if (typeof delta === 'object' && delta !== null && !Array.isArray(delta) && text.trim().length > 0) {
        const obj = delta as Record<string, unknown>;
        const hasBefore = obj.before_emotion !== undefined && obj.before_emotion !== null && String(obj.before_emotion).trim() !== '';
        const hasAfter = obj.after_emotion !== undefined && obj.after_emotion !== null && String(obj.after_emotion).trim() !== '';
        const hasDurability = obj.durability !== undefined && obj.durability !== null;
        if (!hasBefore || !hasAfter || !hasDurability) {
          crossStateWarning = 'Emotional delta should describe before/after shift and durability.';
        }
      }
      return {
        canAdvance: text.trim().length > 10 && hasImprovement,
        reason: !hasImprovement ? 'State how the actor would feel differently' : undefined,
        crossStateWarning,
      };
    }

    case 'CommitmentTested': {
      const decision = sessionState.commitment_decision;
      const text = typeof decision === 'string' ? decision : typeof decision === 'object' && decision !== null
        ? JSON.stringify(decision)
        : '';
      const hasYesNo = /\b(yes|no|would|wouldn't|approve|reject)\b/i.test(text) || text.trim().length > 5;
      const actorScope = getPrimaryActorAuthorityScope(sessionState.actors);
      const situationScope = getSituationScopeHint(sessionState);
      const scopeMismatch =
        actorScope && situationScope &&
        actorScope !== situationScope &&
        !(actorScope === 'global' && (situationScope === 'local' || situationScope === 'regional'));
      const crossStateWarning = scopeMismatch
        ? 'Actor authority scope may not match situation scope.'
        : undefined;
      return {
        canAdvance: text.trim().length > 2 && hasYesNo,
        reason: !hasYesNo ? 'Require explicit yes/no on commitment' : undefined,
        crossStateWarning,
      };
    }

    case 'Initialized':
    case 'EnvironmentScoped':
    case 'MarketFitScored':
    case 'BridgeOptionsSelected':
    case 'Completed':
      return { canAdvance: true };

    default:
      return { canAdvance: true };
  }
}

/** Get weak signal flags for a completed state (for sidebar display). Does not block. */
export function getWeakSignals(
  stateId: string,
  sessionState: Record<string, unknown>
): string[] {
  const signals: string[] = [];
  switch (stateId) {
    case 'SituationValued': {
      const value = sessionState.situation_value;
      const text = typeof value === 'string' ? value : typeof value === 'object' && value !== null ? JSON.stringify(value) : '';
      const hasCostOrFriction = /\b(cost|money|time|risk|friction|constraint)\b/i.test(text);
      if (text.trim().length > 0 && !hasCostOrFriction) signals.push('Low economic signal');
      break;
    }
    case 'EmotionalDeltaEvaluated': {
      const delta = sessionState.emotional_delta;
      const text = typeof delta === 'string' ? delta : typeof delta === 'object' && delta !== null ? JSON.stringify(delta) : '';
      const hasImprovement = /\b(better|improve|relief|confidence|hope|reduce)\b/i.test(text);
      if (text.trim().length > 0 && !hasImprovement) signals.push('Low emotional delta');
      break;
    }
    case 'ActorIdentified': {
      const actors = sessionState.actors;
      const arr = Array.isArray(actors) ? actors : [];
      if (arr.length > 1) {
        const roles = arr.map((a: unknown) => {
          const o = typeof a === 'object' && a !== null ? (a as Record<string, unknown>) : {};
          return String(o.role ?? o.authority ?? '').toLowerCase();
        });
        const hasPayer = roles.some((r) => r.includes('payer'));
        const hasSupplier = roles.some((r) => r.includes('supplier'));
        if (hasPayer && hasSupplier) signals.push('Actor split');
      }
      break;
    }
    case 'ProductSituationMapped': {
      if (!productEffectLinksToSituation(sessionState.product_belief, sessionState)) {
        signals.push('Weak causal link');
      }
      break;
    }
    default:
      break;
  }
  return signals;
}
